import { ReactNode } from 'react';
import { pipe } from 'fp-ts/function';
import { Stack, Card, IconName, Container } from '@optimus-web/core';
import { ModalBodyWrapper, ModalWrapper, withAsyncContainer } from '@mews-commander/ui';
import { ActionType, TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';

interface PayoutReportLayoutProps {
    hasFailed: boolean;
    isFetching: boolean;
    summary: ReactNode;
    children: ReactNode;
    title?: ReactNode;
    breadcrumbs?: Breadcrumbs;
    banner?: ReactNode;
    guideLink: string;
    exportHandler?: () => void;
    scheduleExportHandler?: () => void;
}

const PayoutReportContent = pipe(
    ({ children }: { children: ReactNode }) => (
        <Stack vertical spacing="200">
            {children}
        </Stack>
    ),
    withAsyncContainer
);

export const PayoutReportLayout = ({
    title,
    breadcrumbs,
    hasFailed,
    isFetching,
    summary,
    children,
    banner,
    guideLink,
    exportHandler,
    scheduleExportHandler,
}: PayoutReportLayoutProps) => {
    const actions: HeadingAction[] = [];
    const exportActions: HeadingAction[] = [];

    if (exportHandler) {
        exportActions.push({
            type: ActionType.Link,
            url: '',
            text: <LocalizedText textKey={TextKey.ExportToExcel} />,
            icon: IconName.Exports,
            dataTestId: 'payout-export-to-excel',
            onClick: () => exportHandler(),
        });
    }

    if (scheduleExportHandler) {
        exportActions.push({
            type: ActionType.Link,
            url: '',
            text: <LocalizedText textKey={TextKey.ScheduleRecurrentExport} />,
            icon: IconName.ExportSchedule,
            dataTestId: 'payout-schedule-export',
            onClick: () => scheduleExportHandler(),
        });
    }

    if (exportActions.length === 1) {
        actions.push(exportActions[0]);
    } else if (exportActions.length > 1) {
        actions.push({
            icon: IconName.Exports,
            type: ActionType.Dropdown,
            text: <LocalizedText textKey={TextKey.Export} />,
            items: exportActions,
        });
    }

    actions.push({
        url: guideLink,
        type: ActionType.Link,
        text: <LocalizedText textKey={TextKey.Help} />,
        icon: IconName.Help,
        target: '_blank',
    });

    return (
        <ModalWrapper>
            <ModalHeader title={title} breadcrumbs={breadcrumbs} actions={actions} />
            <ModalBodyWrapper>
                <Container padding="200">
                    <PayoutReportContent hasFailed={hasFailed} isFetching={isFetching}>
                        {banner}
                        {summary}
                        <Card padding="200">{children}</Card>
                    </PayoutReportContent>
                </Container>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
